/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CarouselNavigation from '@/components/molecules/carousel-navigation/carousel-navigation';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import ThemeSearchCard from '@/components/organisms/theme-search-card/theme-search-card';
import CarouselSlideContent from '@/components/utilities/carousel-slide-content/carousel-slide-content';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './search-by-theme.module.scss';

export interface SearchByThemeProps extends ModuleWrapperProps {
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Components['SearchByThemeModuleDto']['aspectRatio'];
  /** An array of links to show */
  ctaLinks?: Content.Components['SearchByThemeModuleDto']['ctaLinks'];
  /** The text to show as a headline */
  headline: Content.Components['SearchByThemeModuleDto']['headline'];
  /** The list of items to show */
  items?: Content.Components['SearchByThemeModuleDto']['items'];
  /** The text to show as a pre headline */
  preHeadline: Content.Components['SearchByThemeModuleDto']['preHeadline'];
  /** The text to show as a sub headline */
  subHeadline: Content.Components['SearchByThemeModuleDto']['subHeadline'];
  /** The text to show as a description */
  text: Content.Components['SearchByThemeModuleDto']['text'];
  /** The variant of the module to use */
  variant?: Content.Components['SearchByThemeModuleDto']['variant'];
}

const SearchByTheme = ({
  aspectRatio = 'threeByFour',
  ctaLinks = [],
  headline,
  items = [],
  preHeadline,
  subHeadline,
  text,
  variant = 'primary',
  ...rest
}: SearchByThemeProps) => {
  const isLaptop = useWindowSize('laptop');
  const [currentIndex, setCurrentIndex] = useState(0);
  const swiperRef = useRef<SwiperInstance>();

  return (
    <ModuleWrapper
      {...rest}
      allowOverflow
      className={classNames(styles.component, {
        [styles.isOffset]: currentIndex !== 0,
        [styles.isPrimary]: variant === 'primary',
        [styles.isSecondary]: variant === 'secondary',
      })}
    >
      <Container>
        <Row className={styles.row}>
          <Column
            className={styles.textColumn}
            width={
              variant === 'primary' ? { desktop: 3, laptop: 4 } : { laptop: 6 }
            }
          >
            {(preHeadline || headline) && (
              <div className={styles.headlineWrapper}>
                {preHeadline && (
                  <Typography
                    className={styles.preHeadline}
                    tag="p"
                    tagStyle="subHeading"
                  >
                    {preHeadline}
                  </Typography>
                )}

                {headline && (
                  <Typography
                    className={styles.headline}
                    tag="h2"
                    tagStyle="displayMedium"
                  >
                    {headline}
                  </Typography>
                )}
              </div>
            )}

            {subHeadline && (
              <Typography
                className={styles.subHeadline}
                tag="p"
                tagStyle="bodyLargeBold"
              >
                {subHeadline}
              </Typography>
            )}

            {text && <RichText text={text} />}

            {ctaLinks.length > 0 && <CtaLinkList items={ctaLinks} />}
          </Column>

          <Column
            className={styles.itemsColumn}
            width={variant === 'primary' ? { desktop: 9, laptop: 8 } : {}}
          >
            <Swiper
              key={`swiper-${isLaptop}`}
              breakpoints={{
                [parseInt(breakpoints.tablet)]: {
                  slidesPerView: 2,
                  spaceBetween: 32,
                  speed: 500,
                },
                [parseInt(breakpoints.desktop)]: {
                  slidesPerView: variant === 'primary' ? 3 : 4,
                  spaceBetween: 32,
                  speed: 750,
                },
              }}
              className={styles.swiper}
              onSlideChangeTransitionEnd={item => {
                setCurrentIndex(item.snapIndex);
              }}
              onSlideChangeTransitionStart={item => {
                setCurrentIndex(item.snapIndex);
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              slidesPerGroup={1}
              slidesPerView={1}
              spaceBetween={0}
              speed={500}
              // Tracks which slides are in view, for CarouselSlideContent
              watchSlidesProgress
            >
              {items.map((item, itemIndex) => (
                <SwiperSlide
                  key={itemIndex}
                  className={classNames(styles.item, {
                    [styles.isActive]: itemIndex === currentIndex,
                  })}
                >
                  <CarouselSlideContent>
                    <div className={styles.itemInner}>
                      <ThemeSearchCard
                        {...item}
                        aspectRatio={aspectRatio ?? 'threeByFour'}
                      />
                    </div>
                  </CarouselSlideContent>
                </SwiperSlide>
              ))}

              <CarouselNavigation
                ariaLabel={headline || undefined}
                showPageCount
                swiperRef={swiperRef}
              />
            </Swiper>
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

SearchByTheme.displayName = 'SearchByTheme';

export default SearchByTheme;
